import React, { useState, useEffect, useRef } from "react";
import {
  ArrowDownCircle,
  Copy,
  QrCode,
  ShieldCheck,
  Info,
  CheckCircle2,
  Clock,
  ExternalLink,
  Sparkles,
  X,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import axios from "axios";
import "./Deposit.css";
import { API_BASE } from "../../../config/api";

const Deposit = () => {
  const [selectedNetwork, setSelectedNetwork] = useState("USDT (TRC-20)");
  const [copied, setCopied] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  
  const [depositAmount, setDepositAmount] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const [countdown, setCountdown] = useState(600);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploadError, setUploadError] = useState("");
  
  const [depositId, setDepositId] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);

  const fileInputRef = useRef(null);

  const networkAddresses = {
    "USDT (TRC-20)": "TQt51XWmGew164eu4enWKgiGaWjfu42Xtf",
    BTC: "bc1qyykuvjnxs7e5qwumgvamh37adttxmk2l2fqayk",
    LTC: "LU38DPtoHiHBk7ynpv6SopMAa3GYwmT4go",
    ETH: "0x8855E3Ce7cCaD98EbEcb5f68B51c4FCee801AC1c",
  };

  const currentTicker = selectedNetwork.split(" ")[0];

  // Load user
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (token) {
      setCurrentUser({ authenticated: true });
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    if (paymentSubmitted && countdown > 0) {
      const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
    if (paymentSubmitted && countdown === 0) {
      console.log("⏰ Countdown expired");
      setPollingActive(false);
      window.location.href = "/#/dashboard/overview";
    }
  }, [paymentSubmitted, countdown, pollingActive]);

  // ✅ Polling - checks for "completed" status
  useEffect(() => {
    if (!depositId || !paymentSubmitted) return;

    console.log("🔄 Polling started for:", depositId);
    setPollingActive(true);

    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
        const { data } = await axios.get(`${API_BASE}/api/deposit/status/${depositId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        console.log("📊 Polling response:", data);

        // ✅ Check for "completed" status
        if (data.status === "completed") {
          console.log("✅ Deposit confirmed!");
          setPollingActive(false);
          localStorage.removeItem('pendingDepositId');
          alert(`✅ Your deposit of $${data.amount} has been approved!\nRedirecting to Overview...`);
          window.location.href = "/#/dashboard/overview";
        }
      } catch (error) {
        console.error("❌ Polling error:", error.response?.data || error.message);
        if (error.response?.status === 404) {
          setPollingActive(false);
          setUploadError("❌ Deposit record not found.");
        }
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);

    return () => {
      clearInterval(interval);
      setPollingActive(false);
    };
  }, [depositId, paymentSubmitted]);

  useEffect(() => {
    return () => {
      if (screenshotPreview?.startsWith("blob:")) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  const handleCopy = () => {
    navigator.clipboard.writeText(networkAddresses[selectedNetwork]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleProceed = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      alert("Please login");
      window.location.href = "/#/login";
      return;
    }
    
    setShowOverlay(true);
    setPaymentSubmitted(false);
    setCountdown(600);
    setDepositAmount("");
    setScreenshotFile(null);
    setScreenshotPreview("");
    setUploadError("");
    setDepositId(null);
    setPollingActive(false);
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setPaymentSubmitted(false);
    setCountdown(600);
    setDepositAmount("");
    setScreenshotFile(null);
    setScreenshotPreview("");
    setUploadError("");
    setDepositId(null);
    setPollingActive(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    setUploadError("");
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Image must be less than 5MB.");
      return;
    }

    setScreenshotFile(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleRemoveScreenshot = () => {
    setScreenshotFile(null);
    if (screenshotPreview?.startsWith("blob:")) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview("");
    setUploadError("");
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handlePaymentConfirmation = async () => {
    const amount = parseFloat(depositAmount);
    
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      alert("Please login");
      window.location.href = "/#/login";
      return;
    }
    
    if (!amount || amount < 200) { 
      alert("Minimum deposit is $200"); 
      return; 
    }
    if (!screenshotFile) { 
      setUploadError("Please upload a screenshot."); 
      return; 
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("amount", amount);
      formData.append("currency", currentTicker);
      formData.append("network", selectedNetwork);
      formData.append("screenshot", screenshotFile);

      const res = await axios.post(`${API_BASE}/api/deposit/notify-admin`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        },
      });

      console.log("✅ Admin notified:", res.data);
      
      if (res.data.depositId) {
        setDepositId(res.data.depositId);
        localStorage.setItem('pendingDepositId', res.data.depositId);
        setPaymentSubmitted(true);
      } else {
        setUploadError("Server did not return a deposit ID.");
      }
      
    } catch (err) {
      console.error("❌ Failed:", err);
      setUploadError(err.response?.data?.message || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="erax-dashboard-wrapper">
      <div className="erax-stats-strip wallet-hero-grid">
        <div className="erax-stat-node wallet-main-card deposit-hero-card">
          <div className="wallet-card-header">
            <span className="erax-node-label">
              <ArrowDownCircle size={14} /> Capital Inbound Vault
            </span>
            <span className="secure-badge">
              <ShieldCheck size={12} /> Institutional Security Enabled
            </span>
          </div>
          <div className="wallet-balance-hero">
            <h2>Instant Crypto Inflow</h2>
            <span className="currency-tag">Zero Fee Entry</span>
          </div>
        </div>
      </div>

      <div className="erax-split-workspace">
        <div className="erax-workspace-panel main-ledger-panel">
          <div className="panel-header-action">
            <div>
              <h3 className="panel-title">Address Settlement Generation</h3>
              <p className="panel-subtitle">Select your preferred asset gateway pipeline</p>
            </div>
          </div>

          <div className="deposit-form-body">
            <div className="form-group custom-deposit-group">
              <label>Select Inbound Gateway Protocol</label>
              <div className="deposit-network-selector-grid">
                {Object.keys(networkAddresses).map((net) => (
                  <button
                    key={net}
                    type="button"
                    className={`network-select-btn ${selectedNetwork === net ? "active-net-btn" : ""}`}
                    onClick={() => setSelectedNetwork(net)}
                  >
                    <span>{net}</span>
                    {net === "USDT (TRC-20)" && (
                      <span className="recommended-pill-badge">
                        <Sparkles size={10} /> Recommended
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="address-generation-display-box">
              <div className="qr-placeholder-wrapper">
                <div className="qr-matrix-box">
                  <QrCode size={130} strokeWidth={1.2} className="text-white" />
                  <div className="qr-center-logo">X</div>
                </div>
              </div>
              <div className="generated-address-details">
                <span className="address-field-header">
                  Your Secured {selectedNetwork} Settlement Address
                </span>
                <div className="address-copy-interactive-row" onClick={handleCopy}>
                  <code className="truncate-address">{networkAddresses[selectedNetwork]}</code>
                  <button className="address-copy-action-icon" type="button">
                    {copied ? <CheckCircle2 size={16} className="text-green" /> : <Copy size={16} className="text-gold" />}
                  </button>
                </div>
                {copied && <span className="copy-success-floating-txt">Copied!</span>}
              </div>
            </div>

            <button className="deposit-proceed-btn" onClick={handleProceed}>Proceed</button>

            <div className="deposit-notice-callout-card">
              <Info size={18} className="text-gold flex-shrink-0" />
              <p>
                Send only <strong>{currentTicker}</strong> to this address.
              </p>
            </div>
          </div>
        </div>

        <div className="erax-workspace-panel analytics-side-panel">
          <div className="inner-analytics-card premium-action-card deposit-tracking-card">
            <h4 className="card-inner-title">
              <Clock size={16} /> Live Network Synchronization
            </h4>
            <p className="terminal-desc">
              Tracking ledger arrivals across decentralized node consensus validation rings.
            </p>
          </div>
        </div>
      </div>

      {showOverlay && (
        <div className="deposit-overlay">
          <div className="deposit-modal">
            <button className="overlay-close-btn" onClick={handleCloseOverlay}>
              <X size={18} />
            </button>

            {!paymentSubmitted ? (
              <>
                <h3>Complete Your Deposit</h3>
                
                <div className="deposit-amount-input-group">
                  <label>Deposit Amount (USD)</label>
                  <input
                    type="number"
                    min="200"
                    step="0.01"
                    placeholder="Minimum $200"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="deposit-amount-input"
                    disabled={loading}
                  />
                  {depositAmount && parseFloat(depositAmount) < 200 && (
                    <span className="amount-error-text">⚠️ Minimum deposit is $200</span>
                  )}
                </div>

                <div className="screenshot-upload-group">
                  <label>Upload Transaction Screenshot</label>
                  
                  {!screenshotPreview ? (
                    <div className="screenshot-upload-box" onClick={handleUploadClick}>
                      <ImageIcon size={24} className="text-gold" />
                      <span>Click to upload screenshot</span>
                      <span className="upload-hint">JPG, PNG, WEBP (max 5MB)</span>
                    </div>
                  ) : (
                    <div className="screenshot-preview-box">
                      <img src={screenshotPreview} alt="Preview" className="screenshot-preview-image" />
                      <button type="button" className="screenshot-remove-btn" onClick={handleRemoveScreenshot} disabled={loading}>
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    className="hidden-file-input"
                    disabled={loading}
                  />
                  {uploadError && <span className="amount-error-text">{uploadError}</span>}
                </div>

                <button
                  className="payment-confirm-btn"
                  onClick={handlePaymentConfirmation}
                  disabled={parseFloat(depositAmount) < 200 || !screenshotFile || loading}
                >
                  {loading ? "Submitting..." : "I Have Sent the Funds"}
                </button>
              </>
            ) : (
              <div className="payment-success-box">
                <CheckCircle2 size={50} className="success-icon" />
                <h3>Request Submitted!</h3>
                <p>Your deposit of ${parseFloat(depositAmount).toFixed(2)} {currentTicker} is pending admin approval.</p>
                
                <div className="countdown-container" style={{ marginTop: "20px" }}>
                  <Clock size={16} className="text-gold" />
                  <span>Admin approval window:</span>
                  <span className="countdown-timer">{formatTime(countdown)}</span>
                </div>
                
                <div style={{ 
                  marginTop: "15px", 
                  padding: "12px", 
                  background: "rgba(74, 222, 128, 0.1)", 
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#4ade80"
                }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {pollingActive ? "🔄 Polling for approval..." : "⏸️ Waiting..."}
                  </p>
                  <p style={{ margin: "6px 0 0 0", fontSize: "11px", opacity: 0.8 }}>
                    Deposit ID: {depositId ? `${depositId.slice(0, 8)}...` : "N/A"}
                  </p>
                </div>
                
                <p className="redirect-text" style={{ color: "#4ade80", fontWeight: "500", marginTop: "15px" }}>
                  ⏳ Waiting for admin approval...<br/>
                  You will be auto-redirected when approved!
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;